import { ChangeEvent, useMemo } from 'react';

// material-ui
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project-imports
import { ThemeMode } from 'config';
import MainCard from 'components/MainCard';
import useConfig from 'hooks/useConfig';

// types
import { PresetColor } from 'types/config';

// assets
import { TickSquare } from 'iconsax-reactjs';

interface ColorProps {
  id: PresetColor;
  primary: string;
  darker: string;
}

/** [id, lightPrimary, lightDarker, darkPrimary, darkDarker] */
const PRESET_THEME_SWATCHES: Array<[PresetColor, string, string, string, string]> = [
  ['theme1', '#3366FF', '#102693', '#305bdd', '#a9c5f8'],
  ['theme2', '#7265E6', '#5549DB', '#655ac8', '#c3baf4'],
  ['theme3', '#068e44', '#001c0f', '#0a7d3e', '#173123'],
  ['theme4', '#3c64d0', '#0d1b5e', '#5d7dcb', '#212841'],
  ['theme5', '#f27013', '#802800', '#d26415', '#f8c48c'],
  ['theme6', '#2aa1af', '#06323d', '#288d99', '#96d0d0'],
  ['theme7', '#00a854', '#003620', '#05934c', '#61ca8b'],
  ['theme8', '#009688', '#002424', '#058478', '#59b8a5']
];

function buildPresetColorOptions(mode: ThemeMode): ColorProps[] {
  const isDark = mode === ThemeMode.DARK;
  const themed = PRESET_THEME_SWATCHES.map(([id, lightP, lightD, darkP, darkD]) => ({
    id,
    primary: isDark ? darkP : lightP,
    darker: isDark ? darkD : lightD
  }));
  return [{ id: 'default', primary: '#4680FF', darker: '#2F63FF' }, ...themed];
}

// ==============================|| CUSTOMIZATION - COLOR SCHEME ||============================== //

export default function ColorScheme() {
  const { mode, presetColor, onChangePresetColor } = useConfig();

  const colorOptions = useMemo(() => buildPresetColorOptions(mode), [mode]);

  const handlePresetColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChangePresetColor(event.target.value as PresetColor);
  };

  return (
    <RadioGroup row aria-label="payment-card" name="payment-card" value={presetColor} onChange={handlePresetColorChange}>
      <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', width: 1 }}>
        {colorOptions.map((color) => (
          <FormControlLabel
            key={color.id}
            control={<Radio value={color.id} sx={{ display: 'none' }} />}
            sx={{ m: 0, width: presetColor === color.id ? '100%' : 'auto', display: 'flex' }}
            slotProps={{ typography: { sx: { flex: 1 } } }}
            label={
              <MainCard
                content={false}
                sx={{
                  bgcolor: color.primary,
                  p: 1,
                  borderRadius: 0.5,
                  borderWidth: 4,
                  borderColor: presetColor === color.id ? color.darker : color.primary,
                  '&:hover': { borderColor: color.darker }
                }}
              >
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', justifyContent: 'center', width: presetColor === color.id ? '100%' : 1, height: 44 }}
                >
                  {presetColor === color.id && (
                    <Stack
                      sx={(theme) => ({
                        alignItems: 'center',
                        color: 'background.paper',
                        ...theme.applyStyles('dark', { color: 'text.primary' })
                      })}
                    >
                      <TickSquare variant="Bulk" />
                      <Typography variant="caption">{color.id}</Typography>
                    </Stack>
                  )}
                </Stack>
              </MainCard>
            }
          />
        ))}
      </Stack>
    </RadioGroup>
  );
}
